import styles from "./CMInfoBillingAddressSubtool.module.css";
import { convertDateStringToMonthDayYearFormat } from "../../../../lib/formatting";
import Collapsible from "../../../common/Collapsible";
import { CMInfoFormInput, CMInfoSubtool } from "../../CMInfo.hooks";
import { Col, Row } from "react-grid-system";
import LabeledCheckbox from "../LabeledCheckbox";
import StyledInput from "../../../common/inputs/StyledInput";
import StyledZipcodeInput from "../../../common/inputs/StyledZipcodeInput";
import { useState } from "react";
import ZipcodeLookupModal from "../../../common/modals/ZipcodeLookupModal/ZipcodeLookupModal";
import {
  ADDRESS_LINE_MAX_LENGTH,
  ADDRESS_WARNING,
  CITY_MAX_LENGTH,
  partialSuccessErrorMessage,
  ZIPCODE_LENGTH,
} from "../../constants";
import Spinner from "../../../common/Spinner";
import Select from "../../../common/inputs/Select";
import { FALLBACK_SUPPORTED_STATES } from "../../../../lib/api/fallback.data";
import {
  CMInfoUpdateSectionType,
  ValidationErrors,
} from "../../../../lib/api/models";
import CMInfoSubtoolButtons from "../CMInfoSubtoolButtons";
import ValidationMessage from "../../../common/ValidationMessage/ValidationMessage";

const strings = {
  billingAddress: "Billing Address",
  addressLine1: "Line 1",
  addressLine2: "Line 2",
  zip: "Zip",
  city: "City",
  state: "State",
  select: "Select",
  lastVerifiedDate: "Last Verified Date",
  lastChangedDate: "Last Addr Chng Dt:",
  nullDate: "XX/XX/XXXX",
  viewScript: "View Script",
  hideScript: "Hide Script",
};

type CMInfoBillingAddressSubtoolProps = {
  className?: string;
  isScriptOpen: boolean;
  onSetScriptOpen: (open: boolean) => void;
  onShowConfirmationModal: (
    variant: "reset" | "clear",
    onSuccess: () => void,
  ) => void;
  showAddressWarning: boolean;
  formState: CMInfoFormInput[CMInfoSubtool.BillingAddress];
  initialFormState: CMInfoFormInput[CMInfoSubtool.BillingAddress];
  generalDispatch: (
    payload: Partial<CMInfoFormInput[CMInfoSubtool.BillingAddress]>,
  ) => void;
  validationMessages: ValidationErrors | undefined;
  lastChangedDate: string;
  onUpdate: () => void;
  currentlyUpdating: CMInfoSubtool | "ALL" | null;
  failedSections: CMInfoUpdateSectionType[];
  startExpanded: boolean;
};

export default function CMInfoBillingAddressSubtool({
  className,
  isScriptOpen,
  onSetScriptOpen,
  onShowConfirmationModal,
  showAddressWarning,
  formState,
  initialFormState,
  generalDispatch,
  validationMessages,
  lastChangedDate,
  onUpdate,
  currentlyUpdating,
  failedSections,
  startExpanded = false,
}: CMInfoBillingAddressSubtoolProps) {
  const [isExpanded, setIsExpanded] = useState(
    formState.billingAddressDueForVerification || startExpanded,
  );
  const [hasUnverifiedChanges, setHasUnverifiedChanges] = useState(false);
  const [zipcodeToLookup, setZipcodeToLookup] = useState<string | null>(null);

  return (
    <Collapsible
      disabled={
        formState.billingAddressDueForVerification ||
        formState.isBillingAddressVerified
      }
      className={className}
      headerText={strings.billingAddress}
      isExpanded={isExpanded}
      onClick={() => {
        setIsExpanded(!isExpanded);
        if (isExpanded) {
          onSetScriptOpen(false);
        }
      }}
    >
      <Row>
        <Col md={5}>
          <StyledInput
            label={strings.addressLine1}
            value={formState.addressLine1}
            onChange={(e) => {
              generalDispatch({
                addressLine1: e.currentTarget.value,
                isBillingAddressVerified: true,
              });
              setHasUnverifiedChanges(true);
            }}
            validationMessages={validationMessages?.addressLine1}
            maxLength={ADDRESS_LINE_MAX_LENGTH}
          />
        </Col>
        <Col md={2}>
          <StyledInput
            label={strings.addressLine2}
            value={formState.addressLine2}
            onChange={(e) => {
              generalDispatch({
                addressLine2: e.currentTarget.value,
                isBillingAddressVerified: true,
              });
              setHasUnverifiedChanges(true);
            }}
            validationMessages={validationMessages?.addressLine2}
            maxLength={ADDRESS_LINE_MAX_LENGTH}
          />
        </Col>
        <Col md={2}>
          <StyledZipcodeInput
            innerContentClassName={styles.label}
            label={
              <div className={styles.zipcodeLabel}>
                {strings.zip}
                {zipcodeToLookup !== null && <Spinner />}
              </div>
            }
            aria-label={strings.zip}
            value={formState.zip}
            onChange={(value) => {
              const oldValue = formState.zip;
              if (
                value.length === ZIPCODE_LENGTH &&
                oldValue.length < ZIPCODE_LENGTH
              ) {
                setZipcodeToLookup(value);
              }
              generalDispatch({
                zip: value,
                isBillingAddressVerified: true,
              });
              setHasUnverifiedChanges(true);
            }}
            validationMessages={validationMessages?.zip}
          />
        </Col>
      </Row>
      <Row className={styles.middleRow}>
        <Col md={3}>
          <StyledInput
            label={strings.city}
            value={formState.city}
            onChange={(e) => {
              generalDispatch({
                city: e.currentTarget.value,
                isBillingAddressVerified: true,
              });
              setHasUnverifiedChanges(true);
            }}
            validationMessages={validationMessages?.city}
            maxLength={CITY_MAX_LENGTH}
          />
        </Col>
        <Col md={3}>
          <Select
            labelClassName={styles.selectLabel}
            inputClassName={styles.select}
            label={strings.state}
            selectedValue={formState.state}
            placeholder={strings.select}
            options={FALLBACK_SUPPORTED_STATES}
            onChange={(value) => {
              generalDispatch({ state: value, isBillingAddressVerified: true });
              setHasUnverifiedChanges(true);
            }}
            validationMessages={validationMessages?.state}
          />
        </Col>
        <Col md={2.5}>
          <LabeledCheckbox
            className={styles.dateInfo}
            label={strings.lastVerifiedDate}
            checkboxLabel={convertDateStringToMonthDayYearFormat(
              formState.billingAddressLastVerifiedDate,
              strings.nullDate,
            )}
            checked={formState.isBillingAddressVerified}
            onCheck={(isBillingAddressVerified) =>
              generalDispatch({ isBillingAddressVerified })
            }
            isExpired={formState.billingAddressDueForVerification}
            disabled={hasUnverifiedChanges}
          />
        </Col>
        <Col md={2.5}>
          <div className={styles.dateInfo}>
            <div className={styles.lastChangedDateLabel}>
              {strings.lastChangedDate}
            </div>
            <div className={styles.lastChangedDateValue}>
              {convertDateStringToMonthDayYearFormat(
                lastChangedDate,
                strings.nullDate,
              )}
            </div>
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <CMInfoSubtoolButtons
            isScriptOpen={isScriptOpen}
            onSetScriptOpen={onSetScriptOpen}
            onReset={() =>
              onShowConfirmationModal("reset", () => {
                generalDispatch(initialFormState);
                setHasUnverifiedChanges(false);
              })
            }
            isResetDisabled={!formState.isBillingAddressVerified}
            onClear={() =>
              onShowConfirmationModal("clear", () => {
                generalDispatch({
                  addressLine1: "",
                  addressLine2: "",
                  city: "",
                  state: "",
                  zip: "",
                  isBillingAddressVerified: true,
                });
                setHasUnverifiedChanges(true);
              })
            }
            isClearDisabled={
              formState.addressLine1 === "" &&
              formState.addressLine2 === "" &&
              formState.city === "" &&
              formState.state === "" &&
              formState.zip === "" &&
              (!formState.isBillingAddressVerified || hasUnverifiedChanges)
            }
            onUpdate={onUpdate}
            isUpdateDisabled={
              !formState.isBillingAddressVerified || !!currentlyUpdating
            }
            isUpdateLoading={currentlyUpdating === CMInfoSubtool.BillingAddress}
          />
        </Col>
      </Row>
      {failedSections.includes("billingAddress") ? (
        <Row>
          <Col>
            <ValidationMessage
              validationMessages={[
                partialSuccessErrorMessage(strings.billingAddress),
              ]}
            />
          </Col>
        </Row>
      ) : null}
      {validationMessages?.general ? (
        <Row>
          <Col>
            <ValidationMessage
              validationMessages={validationMessages.general}
            />
          </Col>
        </Row>
      ) : null}
      {showAddressWarning ? (
        <Row>
          <Col>
            <ValidationMessage validationMessages={[ADDRESS_WARNING]} />
          </Col>
        </Row>
      ) : null}
      <ZipcodeLookupModal
        isOpen={zipcodeToLookup !== null}
        onRequestClose={() => setZipcodeToLookup(null)}
        zipcode={zipcodeToLookup ?? ""}
        onSelectCityState={({ city, state }) => {
          generalDispatch({ city, state, isBillingAddressVerified: true });
          setHasUnverifiedChanges(true);
          setZipcodeToLookup(null);
        }}
      />
    </Collapsible>
  );
}
